import { useQuery } from '@tanstack/react-query';
import { Badge, EmptyState, Panel, t } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { duration, percent } from '../lib/availability';
import { shortDateTime } from '../lib/format';
import { RefusalPage } from './Refusal';
import { ListTable } from './ListTable';

type Interruption = fleetState.paths['/servers/{server}/interruptions']['get']['responses']['200']['content']['application/json']['items'][number];
const TONES = { down: 'neg', scheduled: 'info', unmeasured: 'neutral' } as const;
/** The server page shows the last 30 days; the « Disponibilité » screen goes back 5 years. */
const DAYS = 30;

/**
 * D44 « Interruptions » (server page, Historique tab): every moment of the
 * last 30 days the server was not « up » — unavailable, scheduled stop, or
 * not measured — newest first, with Checkmk's cause, under the period's figure.
 */
export function InterruptionsPanel({ server }: { server: string }) {
  // The period's figure (one row is enough); the rows themselves are the list below (D120).
  const from = new Date(Math.floor(Date.now() / 3_600_000) * 3_600_000 - DAYS * 86_400_000).toISOString();
  const list = useQuery({
    queryKey: ['server-interruptions-figure', server, from],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/interruptions', { params: { path: { server }, query: { from, pageSize: 1 } } })),
    refetchInterval: 60_000,
  });
  if (list.error) return <RefusalPage error={list.error} />;
  const f = list.data?.figure;
  const subtitle = !f ? t('server.interruptions.subtitle', { days: DAYS })
    : f.availability === null ? t('server.interruptions.notMeasured', { days: DAYS })
      : !f.sufficient ? t('server.interruptions.insufficient', { days: DAYS, coverage: percent(f.coverage, 1) })
        : t('server.interruptions.figure', { days: DAYS, availability: percent(f.availability), coverage: percent(f.coverage, 1) });
  return (
    <Panel title={t('server.interruptions.title')} subtitle={subtitle} flush>
      <ListTable<Interruption>
        label={t('server.interruptions.title')}
        queryKey={['server-interruptions', server, from]}
        fetch={async query => unwrap(await api.fleetState.GET('/servers/{server}/interruptions', { params: { path: { server }, query: { ...query, from } as never } }))}
        rowKey={(i: Interruption) => `${i.state}-${i.start}`}
        defaultSort={{ key: 'start', dir: 'desc' }}
        search={t('server.interruptions.search')}
        facets={[
          { key: 'state', label: t('server.interruptions.columns.state'), valueLabel: v => t(`availability.state.${v}`) },
          { key: 'cause', label: t('server.interruptions.columns.cause'), valueLabel: v => t(`availability.cause.${v}`) },
        ]}
        refetchInterval={60_000}
        columns={[
          { key: 'state', label: t('server.interruptions.columns.state'), width: 150, render: (i: Interruption) => <Badge tone={TONES[i.state]}>{t(`availability.state.${i.state}`)}</Badge> },
          { key: 'start', label: t('server.interruptions.columns.window'), width: 200, render: (i: Interruption) => `${shortDateTime(i.start)} → ${i.end ? shortDateTime(i.end) : t('server.interruptions.ongoing')}` },
          { key: 'seconds', label: t('server.interruptions.columns.duration'), width: 100, align: 'right' as const, render: (i: Interruption) => duration(i.seconds) },
          { key: 'cause', label: t('server.interruptions.columns.cause'), grow: true, width: 240, sortable: false, render: (i: Interruption) => (i.cause ? t(`availability.cause.${i.cause}`) : '—') },
        ]}
        empty={<EmptyState framed={false} icon="check_circle" title={t('server.interruptions.empty', { days: DAYS })} />}
      />
    </Panel>
  );
}
